import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base-service';
import { Tools } from '../helpers/tools';
import { ComPlusAdminAccess, ComPlusAdminQuote, ComPlusAdminXml, ComPlusSendResult } from './complus-admin';

@Injectable({
  providedIn: 'root',
})
export class ComPlusAdminService extends BaseService {
  private readonly httpClient = inject(HttpClient);

  private get apiUrl(): string {
    return environment.configurators2d.baseApi + 'ComPlusAdmin/';
  }

  /**
   * Vérifie l'accès à l'écran auprès de l'API (404 si l'accès client n'est pas ouvert).
   * @returns le contexte d'accès, ou `null` si l'accès est refusé.
   */
  public async getAccess(): Promise<ComPlusAdminAccess | null> {
    try {
      return await this.get<ComPlusAdminAccess>(this.apiUrl + 'Access', undefined, undefined, false);
    } catch {
      return null;
    }
  }

  /** Ouvre ou ferme l'écran au site client (administrateur vendeur uniquement). */
  public async setCustomerAccess(enabled: boolean): Promise<ComPlusAdminAccess> {
    return this.put<ComPlusAdminAccess>(this.apiUrl + 'CustomerAccess', { enabled });
  }

  /** Génère le XML COM+ côté API et le renvoie décodé. */
  public async generateXml(quote: ComPlusAdminQuote): Promise<{ filename: string; xml: string }> {
    const result = await this.post<ComPlusAdminXml>(this.apiUrl + 'Xml', quote, undefined, undefined, false);
    return { filename: result.filename, xml: atob(result.xmlBase64) };
  }

  /** Envoie le XML au point d'accès COM+ (proxy de l'environnement), comme le fait la version vendeur. */
  public async send(endpoint: string, xml: string): Promise<ComPlusSendResult> {
    const response = await firstValueFrom(
      this.httpClient.post(endpoint, xml, {
        headers: new HttpHeaders().set('Content-Type', 'text/xml'),
        responseType: 'text',
      })
    );

    return { response, opportunityNumber: this.extractOpportunityNumber(response) };
  }

  private extractOpportunityNumber(response: string): string | undefined {
    try {
      const json: any = Tools.x2js.xml2js(response);
      const id = json?.Envelope?.Body?.ProcessQuote?.DataArea?.Quote?.QuoteHeader?.DocumentReference?.DocumentID?.ID;
      // x2js renvoie une chaîne si l'élément n'a pas d'attribut, un objet { __text } sinon
      return typeof id === 'string' ? id : id?.__text;
    } catch {
      return undefined;
    }
  }
}
